import React, { ReactNode, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import Fuse from 'fuse.js'
import { useParams } from 'react-router-dom'

import { ErrorBoundary } from '../../../../components/common/ErrorBoundary'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ViewBase } from '../../../../components/common/ViewBase'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import { BinderList } from '../../../../components/society/Binder/BinderList/BinderList'
import { binderQueries } from '../../../../api/binder'
import { IBinderModel } from '../../../../interfaces/models/binder.interfaces'
import { unitQueries } from '../../../../api/units'
import { unitString } from '../../../../hooks/useUnitsString'
import { entranceQueries } from '../../../../api/society-entrances'
import { useAuth } from '../../../../hooks/useAuth'

export const BinderView = (): ReactNode => {
  const { userId } = useAuth()
  const { translate } = useAppTranslation()
  const { folderId } = useParams()
  const [searchString, setSearchString] = useState('')
  const { society } = useCurrentSociety()
  const { unitId } = useParams()

  const { data: bindersData, isLoading: isLoadingBinders } = useQuery(
    binderQueries.getBinderPerUnit(society?._id || '', unitId || '', folderId)
  )
  const binders = bindersData?.binderData
  const binderId = bindersData?.binderId || ''

  const { data: units } = useQuery(unitQueries.getUserUnits(userId))
  const societyUnits = units?.filter((su) => su.societyId === society?._id)
  const unit = societyUnits?.find((su) => su._id === unitId)

  const { data: societyEntrences } = useQuery(
    entranceQueries.getEntrancesPerSociety(society?._id)
  )

  const _unitString = unitString(
    unit ? [unit] : [],
    society ? [society] : [],
    societyEntrences || [],
    true
  )

  const rootRoute = 'society:binder-unit'
  const documentRoute = 'society:binder-detail'

  const onChangeSearchString = (
    event: React.FormEvent<HTMLInputElement>
  ): void => {
    setSearchString(event.currentTarget.value)
  }

  const getDocuments = (): IBinderModel[] => {
    return folderId
      ? binders?.filter((doc) => doc.parentId === folderId) || []
      : binders?.filter((doc) => doc.parentId == null) || []
  }
  const documents = getDocuments()

  const isRoot = !folderId
  const currentDocument = folderId
    ? binders?.find((doc) => doc._id === folderId)
    : undefined

  useDocumentTitle(
    isRoot
      ? reverseDocumentTitle('society:binder')
      : reverseDocumentTitle('society:documents-detail', {
          '{{ societyName }}': society?.name,
          '{{ documentName }}': currentDocument?.name,
        })
  )

  const fuseOptions = {
    threshold: 0.3,
    keys: ['name'],
  }
  const fuse = new Fuse(documents, fuseOptions)

  const searchDocuments = fuse.search(searchString).map((result) => result.item)

  return (
    <ErrorBoundary>
      <ViewBase
        searchBar={{ value: searchString, onChange: onChangeSearchString }}
        title={translate('binderView.title')}
        subtitle={_unitString}
        loading={isLoadingBinders}
      >
        <BinderList
          rootRoute={rootRoute}
          folderId={folderId}
          documentRoute={documentRoute}
          documents={searchString === '' ? documents : searchDocuments}
          currentDocument={currentDocument}
          binder={binders || []}
          noSearchResults={searchString !== '' && searchDocuments.length === 0}
          unitId={unitId}
          isAdminView={false}
          binderId={binderId}
        />
      </ViewBase>
    </ErrorBoundary>
  )
}
